import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { buttonVariants } from '@/components/ui/button';
import { MaterialSymbol } from '@/components/ui/material-symbol';

const navItems = [
  { href: '/store', label: 'Store', symbol: 'shopping_bag' as const },
  { href: '/library', label: 'Library', symbol: 'library_books' as const },
  { href: '/community', label: 'Community', symbol: 'forum' as const },
  { href: '/profile', label: 'Profile', symbol: 'person' as const },
];

interface MobileBottomNavProps {
  onOpenFriends?: () => void;
}

export function MobileBottomNav({ onOpenFriends }: MobileBottomNavProps) {
  const { pathname } = useLocation();
  const { profile, signOut } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const isMoreRoute = pathname.startsWith('/settings') || pathname.startsWith('/developer');

  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await signOut();
    } catch (e) {
      console.error(e);
      setTimeout(() => window.location.reload(), 100);
    } finally {
      setSigningOut(false);
      setMoreOpen(false);
    }
  };

  const linkClass = (isActive: boolean) =>
    cn(
      'relative flex min-w-0 flex-1 touch-target flex-col items-center justify-center gap-1 rounded-lg px-1 py-1.5 transition-all',
      isActive ? 'text-nav-active' : 'text-arc-text-muted active:text-nav-active'
    );

  return (
    <>
      <nav
        className="arc-bottom-nav-surface fixed bottom-0 left-0 right-0 z-50 safe-bottom"
        aria-label="Primary"
      >
        <div className="relative flex h-16 items-center justify-center px-1">
          {navItems.map((item) => {
            const isActive = pathname === item.href || pathname?.startsWith(item.href + '/');

            return (
              <Link key={item.href} to={item.href} className={linkClass(isActive)}>
                <div className="relative">
                  <MaterialSymbol name={item.symbol} className="!text-2xl text-current" />
                  {isActive && (
                    <div className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-nav-active/90 ring-2 ring-surface-container" />
                  )}
                </div>
                <span
                  className={cn(
                    'whitespace-nowrap text-center text-xs font-medium',
                    isActive && 'font-semibold'
                  )}
                >
                  {item.label}
                </span>
                {isActive && (
                  <div className="absolute bottom-0 left-1/2 h-0.5 w-7 -translate-x-1/2 rounded-full bg-arc-primary/80" />
                )}
              </Link>
            );
          })}

          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className={linkClass(moreOpen || isMoreRoute)}
            aria-label="More options"
            aria-expanded={moreOpen}
          >
            <div className="relative">
              <MaterialSymbol name="menu" className="!text-2xl text-current" />
            </div>
            <span
              className={cn(
                'whitespace-nowrap text-center text-xs font-medium',
                (moreOpen || isMoreRoute) && 'font-semibold'
              )}
            >
              More
            </span>
          </button>
        </div>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="max-h-[min(85vh,520px)] rounded-t-xl border-t border-arc-border">
          <SheetHeader className="text-left">
            <SheetTitle>More</SheetTitle>
            <SheetDescription className="sr-only">
              Settings, developer area (SDK docs preview · coming soon), and social
            </SheetDescription>
          </SheetHeader>
          <div className="flex flex-col gap-1 safe-bottom">
            <Link
              to="/settings"
              onClick={() => setMoreOpen(false)}
              className={cn(buttonVariants({ variant: 'ghost' }), 'h-12 justify-start text-base font-normal')}
            >
              Settings
            </Link>
            <Link
              to="/developer"
              onClick={() => setMoreOpen(false)}
              className={cn(buttonVariants({ variant: 'ghost' }), 'h-auto min-h-12 justify-start py-3 text-base font-normal')}
            >
              <span className="flex flex-col items-start gap-0.5 leading-snug">
                <span>Developer hub</span>
                <span className="text-xs font-medium text-amber-200/90">SDK docs preview · coming soon</span>
              </span>
            </Link>
            {onOpenFriends && (
              <button
                type="button"
                onClick={() => {
                  setMoreOpen(false);
                  onOpenFriends();
                }}
                className={cn(buttonVariants({ variant: 'ghost' }), 'h-12 justify-start text-base font-normal')}
              >
                Friends
              </button>
            )}
            {profile ? (
              <button
                type="button"
                disabled={signingOut}
                onClick={() => void handleSignOut()}
                className={cn(
                  buttonVariants({ variant: 'ghost' }),
                  'h-12 justify-start text-base font-normal text-arc-danger hover:bg-arc-danger/10 hover:text-arc-danger'
                )}
              >
                <LogOut className="mr-2 h-4 w-4" />
                {signingOut ? 'Signing out…' : 'Sign out'}
              </button>
            ) : null}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
